import Vue from 'vue';
import VueRouter from 'vue-router';
import NProgress from 'nprogress';
import _ from 'underscore';
import store from '@/store';
import AuthService from '@/util/auth.service';
import { loadScript } from '@/util/cdn-loader';
import { isLockedToPortal } from '@/util/cad-incident-utils'
import { setPendingNotice } from '@/util/nav-notice'
import vuetify from '@/plugins/vuetify'
import { canAccess, resolveLandingRoute } from './access'
import * as routes from './routes';
import 'nprogress/nprogress.css';

// Mapa de scripts CDN por ruta. Antes index.html cargaba todos (ol.js,
// xlsx, googleMaps, videosdk) en TODAS las rutas — sumaba ~344 ms de
// EvaluateScript inutil al boot de /map-v2 que no usa ninguno excepto
// videosdk (y solo cuando hay llamada SOS, que se puede lazy-loadear
// dentro del service en vez del shell).
//
// Reglas de carga:
//   ol.js          → /map-legacy (Map.vue OpenLayers) + {cameras,devices,
//                    incidents,sos}-reports (vistas con OL embebido)
//   xlsx           → cualquier vista *-reports (json_to_excel.service)
//   googleMaps     → CRUDs con LocationField/Geofence + /map-legacy
//   videosdk       → /map (SOS calls V2) + /map-legacy (chatSos legacy).
//                    NO se preloadea desde aquí en el caso del modal SOS:
//                    se carga inline en sos-call-service.startVideoCall().
function loadCdnsForRoute(routeName) {
  if (!routeName) return Promise.resolve()
  var promises = []

  // Los routeNames de reportes son `<entidad>-reports` (devices-reports,
  // cameras-reports, etc.), no `reports-<entidad>` — la regex anterior
  // `^reports-?(...)` no matcheaba ninguno y ol.js no se cargaba.
  var needsOl = routeName === 'map-legacy'
    || /^(cameras|devices|incidents|sos)-reports$/i.test(routeName)
  if (needsOl) {
    promises.push(loadScript('/js/ol.js', 'ol'))
    promises.push(loadScript('/js/google_maps_markercluster.js', 'MarkerClusterer'))
  }

  // Matchea la ruta padre 'reports' y todas las hijas *-reports (users-,
  // cameras-, devices-, incidents-, sos-). Antes `^reports` solo cubría
  // la padre.
  // incident-portal exporta xlsx desde HistoryView (usuarios encerrados
  // en esa ruta jamás pasan por *-reports, así que hay que cargarlo aquí).
  var needsXlsx = /(^|-)reports?$/i.test(routeName)
    || routeName === 'incident-portal'
  if (needsXlsx) {
    promises.push(loadScript('/js/xlsx.full.min.js', 'XLSX'))
  }

  var needsGmaps = needsOl
    || /(geofences?|geofence)$/i.test(routeName)
    // map (V2) usa Places en MapSearch.vue para autocompletar direcciones.
    // incident-portal usa Places en NewIncidentForm.vue para búsqueda de ubicación.
    || routeName === 'map'
    || routeName === 'incident-portal'
    // CRUDs que usan LocationField suelen tener nombres como 'cameras',
    // 'sensors', 'devices', 'lpr', 'bus' — se incluye un set explicito
    // para no cargar googleMaps en rutas que no lo necesitan (Home,
    // Profiles, Users, etc.).
    || ['cameras', 'sensors', 'devices', 'lpr', 'bus', 'incident-categories', 'sensor-categories'].indexOf(routeName) >= 0
  if (needsGmaps) {
    if (typeof window !== 'undefined' && !window.gmNoop) {
      window.gmNoop = function() { /* GMaps callback */ }
    }
    promises.push(loadScript('https://maps.googleapis.com/maps/api/js?v=3&key=AIzaSyBmiK3JG37VoUVoDYH12I8Js4H1dloiKWc&libraries=places&callback=gmNoop', 'google'))
  }

  // VideoSDK: necesario en /map (SOS calls V2) + /map-legacy (chatSos
  // legacy). Cargamos eager en beforeEach para que cuando llega una
  // llamada SOS, el global window.VideoSDK ya esté listo (sin esto hay
  // ~100-300 ms de delay extra al conectar la videollamada).
  var needsVideoSdk = routeName === 'map' || routeName === 'map-legacy'
  if (needsVideoSdk) {
    promises.push(loadScript('/js/videosdk.0.0.63.js', 'VideoSDK'))
  }

  // Catch silencioso: si un CDN falla (red), la ruta sigue cargando y
  // las features que dependen del global mostraran su UI degradada (ej.
  // Reports sin XLSX no exporta pero la tabla se ve igual).
  return Promise.all(promises).catch(function(err) {
    // eslint-disable-next-line no-console
    console.warn('[cdn-loader] script failed', err)
  })
}

Vue.use(VueRouter);
const router = new VueRouter({
  mode: 'history',
  linkActiveClass: 'active',
  base: import.meta.env.BASE_URL,
  routes: _.toArray(routes).reduce((acc, val) => acc.concat(val), []),
});

// Router guards
router.beforeEach((to, from, next) => {
  NProgress.start();
  // Fire-and-forget: arranca la descarga de los CDNs requeridos por la
  // ruta target antes de que el AuthService responda. Para cuando el
  // componente target llega a mounted(), los globals (window.ol, XLSX,
  // google) suelen estar listos. Si llegan despues, el componente ya
  // tiene fallbacks (Map.vue espera window.ol, json_to_excel chequea
  // window.XLSX). Sin await aqui: no queremos bloquear la navegacion si
  // un CDN falla en LAN gobierno con internet incierto.
  loadCdnsForRoute(to.name)
  const token = store.getters['auth/getAccessToken'];
  if (to.name !== 'login') {
    if (token) {
      // En paralelo con /session: si apiv2 está caído no se suman las esperas.
      const cargaAccesos = store.dispatch('accesos/cargar')
      AuthService({
        url: '/session',
        method: 'get',
      }).then(async (data) => {
        if (data) {
          store.dispatch('auth/updateProfile', data)
        }
        // Accesos del perfil antes de canAccess: con PERMISOS_POR_PERFIL activo
        // las validaciones de ruta leen el usuario efectivo.
        await cargaAccesos
        const user = store.getters['auth/getUser'];

        // Usuarios de portal de terceros: encerrados en /portal/incidentes.
        // El encierro NO es un permiso, es un candado: va antes que canAccess
        // y se mantiene aparte a propósito. No aplica a administradores (ver
        // isLockedToPortal): encerrar al superusuario lo dejaría sin vía de
        // recuperación por interfaz.
        if (isLockedToPortal(user) && to.name !== 'incident-portal') {
          return next({ name: 'incident-portal' })
        }

        if (canAccess(to, user)) {
          return next()
        }

        // Denegación: en vez de rebotar en silencio a /map, se avisa y se manda
        // al aterrizaje que corresponde al perfil.
        const titulo = to.meta && to.meta.title
        const etiqueta = titulo && vuetify.framework.lang.t(`$vuetify.menu.${titulo}`)
        // Lang.t de Vuetify 2 no lanza si la clave no existe en `menu`: devuelve
        // la clave cruda tal cual (node_modules/vuetify/lib/services/lang/index.js).
        // Sin este chequeo, una ruta con gate pero sin entrada en el locale (ej.
        // /murphy-find, /map-legacy) mostraría un toast con "$vuetify.menu.xxx" en
        // vez de un nombre legible. Si la traducción no resolvió, cae al genérico.
        // `startsWith` y no `indexOf`: Vuetify devuelve la clave completa
        // cuando no resuelve, y siempre empieza por el prefijo. Buscarla en
        // cualquier posición descartaría una etiqueta legítima que casualmente
        // contuviera "$vuetify." dentro del texto.
        const seccion = etiqueta && !etiqueta.startsWith('$vuetify.') ? etiqueta : null
        setPendingNotice({
          title: seccion
            ? `No tienes permiso para acceder a ${seccion}`
            : 'No tienes permiso para acceder a esa sección',
        })
        return next(resolveLandingRoute(user))
      }).catch(() => {
        store.dispatch('auth/logout');
      })
    } else {
      next({ name: 'login' });
    }
  } else if (token) {
    AuthService({
      url: '/session',
      method: 'get',
    }).then(() => {
      const user = store.getters['auth/getUser'];
      if (!to.meta.admin || (user && user.administrator)) {
        next();
      } else {
        next(resolveLandingRoute(user));
      }
    }).catch(() => {
      store.dispatch('auth/logout');
    })
  } else {
    next();
  }
});

router.afterEach(() => {
  NProgress.done();
});

export default router;
